import { useState, useEffect, useMemo, useCallback } from 'react';
import { Auth } from 'aws-amplify';
import { toast } from 'react-toastify';
import appConfig from '../config/app-config';
import useFetch from './useFetch';
import { collateralApi } from '../services/collateralApi';
import { cleanTaxId } from '../components/molecules/TaxIdInput';

const CONVERT_XML_API = appConfig.apis.convertXmlApiEnv;
const PERSON_API = appConfig.apis.personApiEnv;

export interface OperationOption {
    id: string;
    creditOperationsId?: string;
    operationCode: string;
    assignorName: string;
    assignorCnpj: string;
    status: string;
    totalDocuments: number;
    totalReceivables: number;
    totalAmount: number;
    operationType: string;
    obligation: string;
    contractCode: string;
}

export interface PersonResult {
    uuid: string;
    name: string;
    legalName?: string;
    alias: string | null;
    govTaxIdFormatted?: string;
    documents: { type: string; number: string }[];
}

interface Props {
    onCreated: () => void;
}

export default function useCreateAnalysisPageForm({ onCreated }: Props) {
    const [step, setStep] = useState<'select' | 'configure'>('select');
    const [selected, setSelected] = useState<OperationOption | null>(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const [operationCode, setOperationCode] = useState('');
    const [assignorName, setAssignorName] = useState('');
    const [assignorTaxId, setAssignorTaxId] = useState('');
    const [operationType, setOperationType] = useState('CESSAO');
    const [obligationType, setObligationType] = useState('WITHOUT_RECOURSE');

    const [personResults, setPersonResults] = useState<PersonResult[]>([]);
    const [isSearchingPerson, setIsSearchingPerson] = useState(false);
    const [showPersonDropdown, setShowPersonDropdown] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    const fetchHeaders = useMemo(() => [
        { key: 'x-api-key', value: CONVERT_XML_API.key || '' },
        { key: 'Accept-Charset', value: 'utf-8' },
    ], []);
    const { isLoading, data } = useFetch(`${CONVERT_XML_API.address}/operations?page=0&pageSize=100`, fetchHeaders);

    const [existingOpIds, setExistingOpIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        collateralApi.getVerificationConfigs({ page: 1, pageSize: 500 })
            .then((res: any) => {
                const ids = new Set((res.items || []).map((item: any) => item.creditOperationId).filter(Boolean));
                setExistingOpIds(ids as Set<string>);
            })
            .catch(() => {});
    }, []);

    const operations: OperationOption[] = useMemo(() => {
        if (!data?.list) return [];
        return data.list.filter((o: OperationOption & Record<string, unknown>) =>
            !o['sentToValidation'] && !o['collateralAnalysisId'] && !existingOpIds.has(o.id)
        );
    }, [data, existingOpIds]);

    const filteredOps = useMemo(() => {
        let ops = operations;
        if (statusFilter) ops = ops.filter(o => (statusFilter === 'closed') === (o.status === 'closed'));
        if (search) {
            const s = search.toLowerCase();
            ops = ops.filter(o => o.operationCode?.toLowerCase().includes(s) || o.assignorName?.toLowerCase().includes(s));
        }
        return ops;
    }, [operations, search, statusFilter]);

    const handleSelectOperation = (op: OperationOption) => {
        setSelected(op);
        setOperationCode(op.status === 'closed' && op.contractCode ? op.contractCode : op.operationCode || '');
        setAssignorName(op.assignorName || '');
        setAssignorTaxId(op.assignorCnpj || '');
        setOperationType(op.operationType || 'CESSAO');
        setObligationType(op.obligation || 'WITHOUT_RECOURSE');
    };

    const clearPersonSearch = useCallback(() => {
        setPersonResults([]);
        setShowPersonDropdown(false);
    }, []);

    const searchPerson = useCallback(async (query: string) => {
        if (query.length < 3) { clearPersonSearch(); return; }
        setIsSearchingPerson(true);
        try {
            const token = (await Auth.currentSession()).getIdToken().getJwtToken();
            const res = await fetch(`${PERSON_API.address}/legal-persons/page-list?legalName=${encodeURIComponent(query)}&page=0&size=10&sort=id,DESC`, {
                headers: { 'Authorization': `Bearer ${token}`, 'x-api-key': PERSON_API.key || '' },
            });
            if (res.ok) {
                const result = await res.json();
                setPersonResults(result.list || []);
                setShowPersonDropdown(true);
            } else { clearPersonSearch(); }
        } catch { clearPersonSearch(); }
        finally { setIsSearchingPerson(false); }
    }, [clearPersonSearch]);

    const handleSelectPerson = (person: PersonResult) => {
        setAssignorName(person.legalName || person.name);
        const taxId = person.govTaxIdFormatted || person.documents?.find(d => d.type === 'CNPJ')?.number || '';
        if (taxId) setAssignorTaxId(taxId.replace(/[.\-/\s]/g, ''));
        setShowPersonDropdown(false);
    };

    const handleCreate = async () => {
        if (!selected) return;
        setIsCreating(true);
        try {
            const opData = await collateralApi.getOperationData(selected.operationCode, selected.id);
            const documents = opData?.documents || [];
            const receivable_ids = documents.flatMap((d: any) => (d.receivables || []).map((r: any) => r.receivable_id)).filter(Boolean);
            const receivables = documents.flatMap((d: any) =>
                (d.receivables || []).map((r: any) => ({
                    receivable_id: r.receivable_id, nfe_key: d.document_id || '',
                    title_number: d.document_title || '', receivable_number: r.receivable_number || '',
                    debtor_cpf_cnpj: d.debtor_cpf_cnpj || '', debtor_name: d.debtor_name || '',
                    debtor_email: d.debtor_email || '', debtor_phone: d.address_phone || '',
                    city: d.address_city || '', state: d.address_state || '',
                    emission_date: d.emission_date || '', amount: r.amount || 0, due_date: r.due_date || '',
                }))
            ).filter((r: any) => r.receivable_id);

            await collateralApi.createVerificationConfig({
                credit_operation_id: selected.creditOperationsId || selected.id,
                criteria: [{ criteria_type: 'DRAWEE_CONFIRMATION', timing: 'PRE_DISBURSEMENT' }],
                receivable_ids, receivables,
                analysis_mode: 'COMPLETE',
                operation_code: operationCode,
                operation_type: operationType,
                obligation_type: obligationType,
                assignor_name: assignorName,
                assignor_tax_id: cleanTaxId(assignorTaxId),
                total_receivables: selected.totalReceivables || receivable_ids.length,
                total_amount: selected.totalAmount || 0,
            });
            onCreated();
        } catch (error: unknown) {
            const apiError = error as { status?: number };
            if (apiError?.status === 409) toast.warn('Análise já existe para esta operação.');
            else toast.error('Erro ao criar análise. Tente novamente.');
        } finally { setIsCreating(false); }
    };

    return {
        step, setStep, selected, search, setSearch, statusFilter, setStatusFilter,
        operationCode, setOperationCode, assignorName, setAssignorName,
        assignorTaxId, setAssignorTaxId, operationType, setOperationType,
        obligationType, setObligationType,
        personResults, isSearchingPerson, showPersonDropdown,
        isLoading, isCreating, filteredOps,
        handleSelectOperation, searchPerson, handleSelectPerson, handleCreate,
    };
}
